import { useState, useCallback, useEffect } from 'react';
import {
  getSchedulingThresholds,
  fetchSchedulingThresholds,
  saveSchedulingThresholds,
  type SchedulingThresholds,
} from '@/lib/scheduling-thresholds-storage';

export interface UseSchedulingThresholdsReturn {
  thresholds: SchedulingThresholds;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateThreshold: (key: keyof Omit<SchedulingThresholds, 'lastUpdated'>, value: number) => void;
  saveThresholds: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useSchedulingThresholds(): UseSchedulingThresholdsReturn {
  const [thresholds, setThresholds] = useState<SchedulingThresholds>(getSchedulingThresholds);
  const [savedThresholds, setSavedThresholds] = useState<SchedulingThresholds>(getSchedulingThresholds);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSchedulingThresholds().then((fetched) => {
      if (!cancelled) {
        setThresholds(fetched);
        setSavedThresholds(fetched);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges =
    thresholds.fullyResourcedMin !== savedThresholds.fullyResourcedMin ||
    thresholds.partiallyResourcedMin !== savedThresholds.partiallyResourcedMin;

  const updateThreshold = useCallback(
    (key: keyof Omit<SchedulingThresholds, 'lastUpdated'>, value: number) => {
      setThresholds((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveThresholds = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveSchedulingThresholds(thresholds);
      setSavedThresholds(thresholds);
    } finally {
      setIsSaving(false);
    }
  }, [thresholds]);

  const resetToDefaults = useCallback(() => {
    const defaults = { fullyResourcedMin: 95, partiallyResourcedMin: 50 };
    setThresholds((prev) => ({ ...prev, ...defaults }));
  }, []);

  return {
    thresholds,
    hasUnsavedChanges,
    isSaving,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  };
}
